import { useCallback, useEffect, useRef, useState } from 'react'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

export const DEFAULT_ROOMS: Array<App.Occupants> = [{ adults: 2, children: 0, infants: 0 }]

export function isDefaultOccupancy(rooms: Array<App.Occupants>): boolean {
  if (rooms.length !== DEFAULT_ROOMS.length) return false
  return rooms.every((room, i) => {
    const def = DEFAULT_ROOMS[i]
    return (
      room.adults === def.adults &&
      (room.children ?? 0) === (def.children ?? 0) &&
      (room.infants ?? 0) === (def.infants ?? 0) &&
      (!room.childrenAge || room.childrenAge.length === 0)
    )
  })
}

export default function useCarouselOccupancyFilter(
  initialRooms?: Array<App.Occupants>,
  // Notified once the customer applies a real occupancy change, from both handleApply and clear
  // (both write the same "committed" state handleApply does) -- lets a caller that wants to know
  // about every customer-driven occupancy change do so from one place, without needing to override
  // handleApply on this hook's returned object (which defeats memoization on that object, since a
  // new object is a new object every render regardless of what's inside it) or separately wrap
  // clear (which would otherwise bypass any such override entirely).
  //
  // Deliberately NOT called from the prop-sync effect below -- that effect can also change
  // `rooms` (e.g. the itinerary resolving mid-stream to a different default party), but it isn't a
  // customer action, and every other kind in this activity-log family represents one. A caller
  // that needs visibility into prop-driven commits too would need a different signal (e.g. a
  // committed-state fact), not this callback.
  onApplied?: (previousRooms: Array<App.Occupants>, newRooms: Array<App.Occupants>) => void,
) {
  const initial = initialRooms && initialRooms.length > 0 ? initialRooms : DEFAULT_ROOMS
  const [rooms, setRooms] = useState<Array<App.Occupants>>(initial)
  const [pendingRooms, setPendingRooms] = useState<Array<App.Occupants>>(initial)
  const [open, setOpen] = useState(false)
  const [hasUserEdited, setHasUserEdited] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (hasUserEdited) return
    if (initialRooms && initialRooms.length > 0) {
      setRooms(initialRooms)
    } else {
      setRooms(DEFAULT_ROOMS)
    }
  }, [hasUserEdited, initialRooms])

  const handleToggle = useCallback(() => {
    if (!open) {
      setPendingRooms(rooms)
    }
    setOpen(prev => !prev)
  }, [open, rooms])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handlePendingChange = useCallback((nextRooms: Array<App.Occupants | App.RoomOccupants | OfferPageRoom>) => {
    setPendingRooms(nextRooms.map(({ adults, children, childrenAge, infants }) => ({
      adults,
      children,
      childrenAge,
      infants,
    })))
  }, [])

  const handleApply = useCallback(() => {
    setHasUserEdited(true)
    setRooms(pendingRooms)
    setOpen(false)
    onApplied?.(rooms, pendingRooms)
  }, [rooms, pendingRooms, onApplied])

  const clear = useCallback(() => {
    setHasUserEdited(true)
    setRooms(DEFAULT_ROOMS)
    onApplied?.(rooms, DEFAULT_ROOMS)
  }, [rooms, onApplied])

  return {
    rooms,
    pendingRooms,
    open,
    hasUserEdited,
    triggerRef,
    handleToggle,
    handleClose,
    handlePendingChange,
    handleApply,
    clear,
  }
}
